import React from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import { FormControl } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { MultiSelect } from 'react-multi-select-component'

import { MULTI_SELECT_STRINGS } from 'consts/strings'
import { UploadButton } from 'components'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'

import { useStyles } from '../../../ManualRewards/components/CreateManualRewardDialog/styles'
import { EmailChipsInput } from '../EmailChipsInput'

export interface TagOption {
  label: string
  value: number
}

interface VoucherSegmentationTabProps {
  tagsForSelect: TagOption[]
  tagsSelected: TagOption[]
  onTagsChange: (tags: TagOption[]) => void
  excludedTagsSelected: TagOption[]
  onExcludedTagsChange: (tags: TagOption[]) => void
  allTagsAreRequired: boolean
  onAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement>
  emailChips: string[]
  onEmailChipsChange: (emails: string[]) => void
  onEmailCsvChange: (file: File) => void
  onEmailCsvClear: () => void
  emailCsvFile: File | null
  csvEmailCount: number
  isLoading: boolean
  // eslint-disable-next-line react/require-default-props
  readOnly?: boolean
}

const VoucherSegmentationTab: React.FC<VoucherSegmentationTabProps> = ({
  tagsForSelect,
  tagsSelected,
  onTagsChange,
  excludedTagsSelected,
  onExcludedTagsChange,
  allTagsAreRequired,
  onAllTagsAreRequiredChange,
  emailChips,
  onEmailChipsChange,
  onEmailCsvChange,
  onEmailCsvClear,
  emailCsvFile,
  csvEmailCount,
  isLoading,
  readOnly = false,
}) => {
  const classes = useStyles()
  const baseDisabled = isLoading || readOnly

  const hasTags = tagsSelected.length > 0 || excludedTagsSelected.length > 0
  const hasEmails = emailChips.length > 0
  const hasCsv = Boolean(emailCsvFile) && csvEmailCount > 0

  const tagsDisabled = baseDisabled || hasEmails || hasCsv
  const emailsDisabled = baseDisabled || hasTags || hasCsv
  const csvDisabled = baseDisabled || hasTags || hasEmails

  const includeTagOptions = tagsForSelect.filter(
    tag => !excludedTagsSelected.some(selected => selected.value === tag.value),
  )
  const excludeTagOptions = tagsForSelect.filter(
    tag => !tagsSelected.some(selected => selected.value === tag.value),
  )

  return (
    <Grid container spacing={3}>
      {readOnly && (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            La segmentación no puede modificarse después de crear el voucher.
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography className={classes.dialogSubTitle}>Tags a incluir</Typography>
        <FormControl className={classes.fullWidth}>
          <MultiSelect
            options={includeTagOptions}
            value={tagsSelected}
            onChange={onTagsChange}
            labelledBy="Seleccionar Tags"
            hasSelectAll={false}
            className={`${classes.outlinedMultiSelect}${tagsDisabled ? ' disabled' : ''}`}
            overrideStrings={MULTI_SELECT_STRINGS}
            disabled={tagsDisabled}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              onChange={onAllTagsAreRequiredChange}
              checked={allTagsAreRequired}
              disabled={tagsDisabled}
            />
          }
          label="Sólo usuarios con todos los tags seleccionados a incluir"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography className={classes.dialogSubTitle}>Tags a excluir</Typography>
        <FormControl className={classes.fullWidth}>
          <MultiSelect
            options={excludeTagOptions}
            value={excludedTagsSelected}
            onChange={onExcludedTagsChange}
            labelledBy="Seleccionar Tags a excluir"
            hasSelectAll={false}
            className={`${classes.outlinedMultiSelect}${tagsDisabled ? ' disabled' : ''}`}
            overrideStrings={MULTI_SELECT_STRINGS}
            disabled={tagsDisabled}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.dialogSubTitle}>Segmentar por Correo Electrónico</Typography>
        <EmailChipsInput
          emails={emailChips}
          onChange={onEmailChipsChange}
          disabled={emailsDisabled}
          label=""
        />
        <Typography
          variant="caption"
          display="block"
          color="textSecondary"
          style={{ marginTop: 4 }}
        >
          Presioná Enter, coma o espacio para agregar cada correo.
        </Typography>
      </Grid>

      {!readOnly && (
        <Grid item xs={12}>
          <Typography className={classes.dialogSubTitle} style={{ marginBottom: 12 }}>
            Carga masiva por CSV
          </Typography>
          <UploadButton
            id="voucher-email-csv-upload"
            accept=".csv"
            label="Cargar archivo CSV"
            onChange={onEmailCsvChange}
            maxSizeInMB={5}
            disabled={csvDisabled}
          />
          {emailCsvFile && (
            <Grid container alignItems="center" spacing={1} style={{ marginTop: 8 }}>
              <Grid item xs>
                <Typography variant="caption" display="block">
                  {emailCsvFile.name} — {csvEmailCount} email(s) detectado(s)
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  size="small"
                  onClick={onEmailCsvClear}
                  disabled={isLoading}
                  aria-label="Eliminar archivo CSV"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export { VoucherSegmentationTab }
